import type { MillsheetSortConfig } from '@mbfab/shared'
import { isSome, numberUtil, sortMillsheetItems } from '@mbfab/shared'
import { groupBy, sum } from 'es-toolkit'

import type { ColumnConfig } from '../../order-matching/print/svg/main-row'
import type { Item } from '../../order-matching/print/svg/millsheet-list-svg'
import type { MillsheetRow } from '../types'

//
// ホーム（ミルシート一覧）の印刷・Excel共通の行データ組み立て。
// v1 apps/frontend/client/src/components/MillsheetPrintForm/PrintGroupedMilsheet.tsx の
// asSummary=true 分岐の移植（一覧表 asSummary=false は v1 側で既に deprecated のため移植しない）。
//

type RowItem = Omit<Item, 'weight' | 'quantity'> & { weight: number; quantity: number | null }

const EMPTY_ITEM: Item = {
  steelType: '',
  sectionType: '',
  size: '',
  thicknessDiameter: null,
  length: '',
  quantity: '',
  weight: '',
  heatNumber: '',
  individualNumber: '',
  no: '',
  manufacturerName: '',
  constructionArea: '',
  member: '',
  buildingPart: '',
  memo: '',
  pageNumber: null,
  tradingCompany: null,
  orderNumber: null,
  innerNo: null,
}

// 明細1件 = 1行で返す。まとめる単位は「表示項目の値が同じ明細」であり、表示項目はここでは分からないため
// buildSummaryItems 側で決める（ここで寸法単位にまとめると、長さ違いがカンマ連結で潰れてしまう）
export function buildPrintRows(millsheets: readonly MillsheetRow[]): RowItem[] {
  return millsheets.flatMap((millsheet) => {
    if (millsheet.millsheetItems.length === 0) {
      return [
        {
          ...EMPTY_ITEM,
          no: millsheet.no,
          manufacturerName: millsheet.manufacturerName,
          tradingCompany: millsheet.tradingCompany,
          orderNumber: millsheet.orderNumber,
          innerNo: millsheet.innerNo,
          quantity: null,
          weight: 0,
        },
      ]
    }
    return millsheet.millsheetItems.map((steel): RowItem => ({
      steelType: steel.steelType,
      sectionType: steel.sectionType,
      size: steel.size,
      thicknessDiameter: steel.thicknessDiameter,
      length: steel.length,
      quantity: steel.quantity,
      weight: steel.weightBeforeProcessing ?? 0,
      heatNumber: steel.heatNumber,
      individualNumber: steel.individualNumber,
      no: millsheet.no,
      manufacturerName: millsheet.manufacturerName,
      constructionArea: steel.constructionArea,
      member: steel.member,
      buildingPart: steel.buildingPart,
      memo: '',
      pageNumber: null,
      tradingCompany: millsheet.tradingCompany,
      orderNumber: millsheet.orderNumber,
      innerNo: millsheet.innerNo,
    }))
  })
}

// 集計表として表示する行データを組み立てる（v1 PrintGroupedMilsheet の asSummary=true 分岐の移植）。
//  - 表示項目設定（weight・quantity以外）でグルーピングし、数量と重量の合計を集計
//  - 表示項目設定の並び順でソート
//  - 表示項目設定の1項目目が変わるたびに「小計」行を挿入（小計の下は1行あける）
//  - 最後に「合計」行を追加
export function buildSummaryItems(
  rows: RowItem[],
  columnConfigs: ColumnConfig[],
  sortConfig: MillsheetSortConfig,
): Item[] {
  const activeConfigs = columnConfigs.filter((x) => x.active)
  const columnKeys = activeConfigs.map((x) => x.type)
  const groupByKeys = columnKeys.filter((x) => x !== 'weight' && x !== 'quantity')

  // 値に '-' を含む寸法（H-300x150 等）があっても別グループが衝突しないよう、区切り文字連結ではなく JSON にする
  const summarizedItems = Object.values(
    groupBy(rows, (x) =>
      JSON.stringify(
        groupByKeys.map((key) => {
          const value = x[key]
          return isSome(value) ? String(value) : ''
        }),
      ),
    ),
  ).map((values) => {
    const first = values[0] ?? { ...EMPTY_ITEM, quantity: null, weight: 0 }
    const weight = sum(values.map((x) => x.weight))
    const quantities = values.map((x) => x.quantity).filter(isSome)
    return {
      ...first,
      // 明細0件のプレースホルダー行だけのグループは数量を空欄にする（0本と誤読させないため）
      quantity: quantities.length === 0 ? '' : numberUtil.numberWithDelimiterFilter(sum(quantities).toString()),
      originalWeight: weight,
      weight: numberUtil.numberWithDelimiterFilter(weight.toString()),
    }
  })

  const sortedSummarizedItems = sortMillsheetItems(summarizedItems, sortConfig, groupByKeys)

  const firstGroupKey = columnKeys[0] ?? 'steelType'
  const itemsWithSubtotal = Object.values(
    groupBy(sortedSummarizedItems, (x) => {
      const value = x[firstGroupKey]
      return isSome(value) ? String(value) : ''
    }),
  ).flatMap((items) => {
    const subtotalItem: Item = {
      ...EMPTY_ITEM,
      weight: numberUtil.numberWithDelimiterFilter(sum(items.map((x) => x.originalWeight)).toString()),
    }
    return [...items, subtotalItem, EMPTY_ITEM]
  })

  const totalItem: Item = {
    ...EMPTY_ITEM,
    weight: numberUtil.numberWithDelimiterFilter(sum(summarizedItems.map((x) => x.originalWeight)).toString()),
  }

  return [...itemsWithSubtotal, totalItem]
}
